import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';

import { Button } from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthErrorMessage } from '../../utils/authErrors';
import type { AuthStackParamList } from '../../navigation/types';

type Props = NativeStackScreenProps<AuthStackParamList, 'Login'>;

export function LoginScreen({ navigation }: Props) {
  const { login, signInAsGuest } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [guestBusy, setGuestBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onGuest() {
    setError(null);
    setGuestBusy(true);
    try {
      await signInAsGuest();
    } catch (e: unknown) {
      setError(getAuthErrorMessage(e));
    } finally {
      setGuestBusy(false);
    }
  }

  async function onSubmit() {
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
    } catch (e: unknown) {
      setError(getAuthErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      className="flex-1 bg-white dark:bg-black">
      <ScrollView
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: 28,
          paddingTop: 48,
          paddingBottom: 40,
        }}>
        <Text className="mb-2 text-3xl font-bold text-zinc-900 dark:text-zinc-50">Hoş geldin</Text>
        <Text className="mb-10 text-base text-zinc-500 dark:text-zinc-400">
          Annedaş’a giriş yap, ebeveynlerle bağlantı kur.
        </Text>

        {error ? (
          <View className="mb-6 rounded-2xl border border-zinc-200 bg-zinc-50 px-5 py-4 dark:border-zinc-800 dark:bg-zinc-900">
            <Text className="text-center text-sm text-zinc-800 dark:text-zinc-200">{error}</Text>
          </View>
        ) : null}

        <Text className="mb-2 font-medium text-zinc-900 dark:text-zinc-100">E-posta</Text>
        <TextInput
          className="mb-5 rounded-2xl border border-zinc-200 bg-white px-5 py-4 text-base text-zinc-900 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-100"
          placeholder="ornek@email.com"
          placeholderTextColor="#9ca3af"
          autoCapitalize="none"
          keyboardType="email-address"
          autoCorrect={false}
          value={email}
          onChangeText={t => {
            setError(null);
            setEmail(t);
          }}
        />

        <Text className="mb-2 font-medium text-zinc-900 dark:text-zinc-100">Şifre</Text>
        <TextInput
          className="mb-8 rounded-2xl border border-zinc-200 bg-white px-5 py-4 text-base text-zinc-900 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-100"
          placeholder="••••••••"
          placeholderTextColor="#9ca3af"
          secureTextEntry
          value={password}
          onChangeText={t => {
            setError(null);
            setPassword(t);
          }}
        />

        <Button disabled={busy} loading={busy} onPress={() => void onSubmit()}>
          {busy ? 'Giriş yapılıyor…' : 'Giriş Yap'}
        </Button>

        <TouchableOpacity
          activeOpacity={0.7}
          onPress={() => navigation.navigate('Register')}
          className="py-4">
          <Text className="text-center text-base text-zinc-500">
            Hesabın yok mu? <Text className="font-semibold text-zinc-900 dark:text-zinc-100">Kayıt Ol</Text>
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          activeOpacity={0.7}
          onPress={() => navigation.navigate('PhoneAuth')}
          className="py-2">
          <Text className="text-center text-sm text-zinc-500">Telefon ile devam et</Text>
        </TouchableOpacity>

        <TouchableOpacity
          activeOpacity={0.75}
          onPress={() => void onGuest()}
          disabled={busy || guestBusy}
          className="mt-6 py-3">
          <Text className="text-center text-sm text-zinc-500 underline decoration-zinc-300 dark:text-zinc-400 dark:decoration-zinc-600">
            {guestBusy ? 'Misafir olarak açılıyor…' : 'Kayıt Olmadan Misafir Olarak Gez'}
          </Text>
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}
